<script setup>
import { ref, computed } from "vue";
import MaxButton from "@/Components/MaxButton.vue";

/**
 * Закрытый фрагмент статьи: вместо текста — что внутри и форма для кода.
 * Сам текст сюда не приходит: сервер вырезает его до отправки страницы (ArticleHtml::applyPaywall).
 */
const props = defineProps({
  articleId: { type: Number, required: true },
  words: { type: Number, default: 0 },
  headings: { type: Array, default: () => [] },
  maxLink: { type: String, default: "" },
  lawyerName: { type: String, default: "" },
  articleTitle: { type: String, default: "" },
  articleUrl: { type: String, default: "" },
});
const emit = defineEmits(["call"]);

const code = ref("");
const sending = ref(false);
const error = ref("");

const minutes = computed(() => Math.max(1, Math.round(props.words / 180)));

// Готовый запрос юристу: копируется при нажатии «Написать в MAX»
const requestText = computed(() =>
  "Здравствуйте! Прошу предоставить код для открытия скрытого фрагмента в статье «" + props.articleTitle + "»: " + props.articleUrl);
const copyState = ref(null); // null | true (скопировано) | false (не удалось — покажем текст)
async function copyAgain() {
  try { await navigator.clipboard.writeText(requestText.value); copyState.value = true; } catch (e) { copyState.value = false; }
}
const inputId = computed(() => "paywall-code-" + props.articleId + "-" + Math.random().toString(36).slice(2, 7));

async function unlock() {
  const value = code.value.replace(/\s+/g, "");
  if (!value) {
    error.value = "Введите код — его присылает юрист.";
    return;
  }
  sending.value = true;
  error.value = "";
  try {
    const token = document.querySelector('meta[name="csrf-token"]')?.content;
    const res = await fetch(`/articles/${props.articleId}/unlock`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-TOKEN": token || "", "X-Requested-With": "XMLHttpRequest" },
      credentials: "same-origin",
      body: JSON.stringify({ code: value }),
    });
    const data = await res.json().catch(() => ({}));
    if (res.status === 429) throw new Error("Слишком много попыток. Подождите минуту и попробуйте снова.");
    if (!res.ok || !data.ok) throw new Error(data.error || "Код не подошёл.");
    try { window.goal && window.goal("PAYWALL_UNLOCK"); } catch (e) {}
    window.location.reload();
  } catch (e) {
    error.value = e.message;
    sending.value = false;
  }
}
</script>

<template>
  <div class="paywall-card">
    <!-- Размытые строки — намёк на текст, которого здесь нет; поверх — что это и почему скрыто -->
    <div class="paywall-card__cover">
      <div class="paywall-card__ghost" aria-hidden="true">
        <span style="width: 96%"></span><span style="width: 88%"></span><span style="width: 92%"></span>
        <span style="width: 78%"></span><span style="width: 94%"></span><span style="width: 64%"></span>
      </div>
      <div class="paywall-card__explain">
        <p class="paywall-card__title">Скрытый фрагмент статьи</p>
        <p class="paywall-card__why">
          Здесь самое практичное: разбор, порядок действий, шаблоны. Автор скрыл эту часть, чтобы её не копировали
          на другие сайты без указания источника, а читатель получал её напрямую от юриста — с возможностью задать вопрос.
        </p>
        <p class="paywall-card__how">Код откроет автор статьи{{ lawyerName ? " — " + lawyerName : "" }}.</p>
      </div>
    </div>

    <div class="paywall-card__body">
      <p class="text-base text-gray-800">
        Ещё {{ words.toLocaleString("ru-RU") }} слов, около {{ minutes }} мин чтения.
        <template v-if="headings.length">Внутри:</template>
      </p>
      <ul v-if="headings.length" class="mt-1 list-none space-y-1 p-0 text-sm text-gray-700">
        <li v-for="(h, i) in headings" :key="i" class="flex gap-2"><span class="text-indigo-400">—</span>{{ h }}</li>
      </ul>

      <div class="mt-4 grid gap-3 sm:grid-cols-2 sm:items-end">
        <div>
          <p class="mb-2 text-sm text-gray-600">1. Получите код у юриста{{ maxLink ? " в MAX" : "" }}</p>
          <MaxButton v-if="maxLink" :href="maxLink" goal="PAYWALL_MAX" compact class="w-full" :copy-text="requestText" @copied="copyState = $event" />
          <button v-else type="button" class="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50" @click="emit('call')">
            Узнать код по телефону
          </button>
        </div>
        <form @submit.prevent="unlock">
          <label :for="inputId" class="mb-2 block text-sm text-gray-600">2. Введите код</label>
          <div class="flex gap-2">
            <input :id="inputId" v-model="code" type="text" inputmode="text" autocomplete="one-time-code" maxlength="16" placeholder="Код"
              class="min-w-0 flex-1 rounded-lg border-gray-300 text-center font-mono text-base uppercase tracking-widest focus:border-indigo-500 focus:ring-indigo-500" />
            <button type="submit" :disabled="sending" class="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-60">
              {{ sending ? "…" : "Открыть" }}
            </button>
          </div>
        </form>
      </div>
      <!-- После нажатия «Написать в MAX»: текст запроса уже в буфере — осталось вставить в чат -->
      <div v-if="copyState !== null" class="mt-3 rounded-lg border border-indigo-100 bg-indigo-50/60 px-3 py-2 text-sm text-gray-700" role="status">
        <p class="font-medium text-indigo-800">
          {{ copyState ? "Текст запроса скопирован — вставьте его в чат MAX" : "Скопируйте текст и отправьте юристу в MAX" }}
        </p>
        <p class="mt-1 text-xs text-gray-600 break-words">{{ requestText }}</p>
        <button type="button" class="mt-1 text-xs font-medium text-indigo-700 hover:underline" @click="copyAgain">Скопировать ещё раз</button>
      </div>
      <p v-if="error" class="mt-3 rounded-lg bg-rose-50 px-3 py-2 text-sm text-rose-700" role="alert">{{ error }}</p>
      <p class="mt-3 text-xs text-gray-500">Код вводится один раз: на этом устройстве статья останется открытой 90 дней.</p>
    </div>
  </div>
</template>

<style>
.paywall-card { position: relative; border: 1px solid #e0e7ff; border-radius: .75rem; overflow: hidden; background: #fff; }
.paywall-card__cover { position: relative; }
/* Размытые строки — фоном под объяснением: высоту карточки задаёт текст, а не строки,
   иначе на узкой колонке объяснение вылезало бы за размытую область */
.paywall-card__ghost {
  position: absolute; inset: 0; display: grid; align-content: start; gap: .75rem; padding: 1.25rem;
  filter: blur(2.5px); opacity: .8; overflow: hidden;
}
.paywall-card__explain {
  position: relative; display: flex; flex-direction: column; gap: .35rem;
  padding: 1.5rem 1.25rem .75rem; background: linear-gradient(180deg, rgb(255 255 255 / .35), rgb(255 255 255 / .8) 70%, #fff);
}
.articlebody .paywall-card__title { display: flex; align-items: center; gap: .4rem; font-size: 1rem; font-weight: 800; color: #312e81; }
.paywall-card__title::before {
  content: ""; width: 1.1rem; height: 1.1rem; flex: none; background: currentColor;
  -webkit-mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M7 11V8a5 5 0 0110 0v3M6 11h12v9H6z'/%3E%3C/svg%3E") center / contain no-repeat;
          mask: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='black' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M7 11V8a5 5 0 0110 0v3M6 11h12v9H6z'/%3E%3C/svg%3E") center / contain no-repeat;
}
.articlebody .paywall-card__why { font-size: .875rem; line-height: 1.5; color: #374151; }
.articlebody .paywall-card__how { font-size: .875rem; font-weight: 600; color: #4338ca; }
.paywall-card__ghost span { display: block; height: .75rem; border-radius: 9999px; background: #c7d2fe; }
.paywall-card__body { position: relative; padding: .5rem 1.25rem 1.25rem; }
/* Карточка живёт внутри .articlebody — гасим типографику статьи (абзацные отступы, списки, ссылки) */
.articlebody .paywall-card p { margin: 0; }
.articlebody .paywall-card ul { display: block; margin: .25rem 0 0; padding: 0; list-style: none; }
.articlebody .paywall-card li { margin: .25rem 0; padding: 0; }
.articlebody .paywall-card a { color: inherit; text-decoration: none; }
.articlebody .paywall-card .grid p { margin-bottom: .5rem; }
</style>
